import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';

// ─── Palette ──────────────────────────────────────────────────
const ORANGE      = '#FF6B2C';
const ORANGE_DARK = '#c2410c';
const ORANGE_MIST = '#fff7ed';
const GRAY_900    = '#111827';
const GRAY_700    = '#374151';
const GRAY_500    = '#6b7280';
const GRAY_300    = '#d1d5db';
const GRAY_100    = '#f3f4f6';
const WHITE       = '#ffffff';

// ─── Label Map ────────────────────────────────────────────────
const labels = {
  en: {
    title:       'REQUEST FOR INFORMATION',
    details:     'DETAILS',
    subject:     'SUBJECT',
    profession:  'PROFESSION',
    question:    'QUESTION',
    response:    'RESPONSE',
    attachments: 'ATTACHMENTS',
    rfiNo:       'RFI NO.',
    noSubject:   'No subject provided',
    noQuestion:  'No question provided.',
    page:        (n, t) => `Page ${n} of ${t}`,
  },
  fr: {
    title:       'DEMANDE D\'INFORMATION',
    details:     'DÉTAILS',
    subject:     'SUJET',
    profession:  'PROFESSION',
    question:    'QUESTION',
    response:    'RÉPONSE',
    attachments: 'PIÈCES JOINTES',
    rfiNo:       'QRT NO.',
    noSubject:   'Aucun sujet fourni',
    noQuestion:  'Aucune question fournie.',
    page:        (n, t) => `Page ${n} sur ${t}`,
  },
};

// ─── Styles ───────────────────────────────────────────────────
const styles = StyleSheet?.create({

  // ── Page ──
  page: {
    fontFamily: 'Helvetica',
    backgroundColor: WHITE,
    paddingBottom: 56,
  },

  // ── Header band ──
  header: {
    backgroundColor: ORANGE,
    paddingHorizontal: 28,
    paddingTop: 18,
    paddingBottom: 18,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flexDirection: 'column',
    flex: 1,
  },
  logoImage: {
    width: 110,
    maxHeight: 44,
    objectFit: 'contain',
    objectPositionX: 'left',
    marginBottom: 4,
  },
  headerRight: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    minWidth: 110,
  },
  companyName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 17,
    color: WHITE,
    letterSpacing: 0.3,
  },
  projectName: {
    fontSize: 9,
    color: '#fde8d0',
    marginTop: 3,
  },
  projectNumber: {
    fontSize: 8.5,
    color: '#fde8d0',
    marginTop: 1,
  },
  rfiNumberLabel: {
    fontSize: 7.5,
    color: '#fde8d0',
    letterSpacing: 0.8,
    marginBottom: 2,
    textAlign: 'right',
  },
  rfiNumber: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 15,
    color: WHITE,
    textAlign: 'right',
  },
  rfiDate: {
    fontSize: 8.5,
    color: '#fde8d0',
    marginTop: 3,
    textAlign: 'right',
  },

  // ── Accent line under header ──
  accentLine: {
    height: 3,
    backgroundColor: ORANGE_DARK,
  },

  // ── Title bar ──
  titleBar: {
    backgroundColor: GRAY_100,
    paddingHorizontal: 28,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: GRAY_300,
    alignItems: 'center',
  },
  titleText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
    color: GRAY_900,
    letterSpacing: 2,
    textAlign: 'center',
  },

  // ── Body ──
  body: {
    paddingHorizontal: 28,
    paddingTop: 20,
  },

  // ── Section divider label ──
  sectionDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    marginTop: 4,
  },
  sectionDividerBar: {
    width: 3,
    height: 12,
    backgroundColor: ORANGE,
    borderRadius: 2,
    marginRight: 6,
  },
  sectionDividerText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7.5,
    color: GRAY_500,
    letterSpacing: 1,
  },

  // ── Subject / Profession row ──
  subjectProfessionRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16,
  },
  subjectBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: GRAY_300,
    borderRadius: 5,
    padding: 10,
    backgroundColor: WHITE,
  },
  professionBox: {
    width: '38%',
    borderWidth: 1,
    borderColor: GRAY_300,
    borderRadius: 5,
    padding: 10,
    backgroundColor: WHITE,
  },
  fieldLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7,
    color: ORANGE,
    letterSpacing: 1,
    marginBottom: 4,
  },
  fieldValue: {
    fontSize: 10.5,
    color: GRAY_900,
    lineHeight: 1.4,
  },
  fieldValueEmpty: {
    fontSize: 10.5,
    color: GRAY_300,
    lineHeight: 1.4,
  },

  // ── Question box ──
  questionSection: {
    marginBottom: 16,
  },
  questionBox: {
    borderWidth: 1.5,
    borderColor: ORANGE,
    borderRadius: 5,
    padding: 12,
    backgroundColor: ORANGE_MIST,
    minHeight: 80,
  },
  questionText: {
    fontSize: 10,
    color: GRAY_700,
    lineHeight: 1.6,
  },

  // ── Response box ──
  responseSection: {
    marginBottom: 16,
  },
  responseBox: {
    borderWidth: 1,
    borderColor: GRAY_300,
    borderRadius: 5,
    padding: 10,
    backgroundColor: WHITE,
    minHeight: 200,
  },
  responsePlaceholder: {
    fontSize: 9,
    color: GRAY_300,
    fontFamily: 'Helvetica-Oblique',
  },
  responseLines: {
    marginTop: 8,
  },
  line: {
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    marginBottom: 10,
  },

  // ── Attachments ──
  attachmentsSection: {
    marginBottom: 16,
  },
  attachmentImage: {
    width: '100%',
    marginBottom: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: GRAY_300,
  },

  // ── Footer ──
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: ORANGE,
    paddingHorizontal: 28,
    paddingVertical: 9,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerCompany: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    color: WHITE,
    letterSpacing: 0.3,
  },
  footerPage: {
    fontSize: 8,
    color: '#fde8d0',
  },
});

// ─── Helpers ──────────────────────────────────────────────────
const formatDate = (iso, language = 'en') => {
  if (!iso) return '—';
  try {
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(iso)?.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

// ─── Sub-components ───────────────────────────────────────────
const SectionLabel = ({ label }) => (
  <View style={styles?.sectionDivider}>
    <View style={styles?.sectionDividerBar} />
    <Text style={styles?.sectionDividerText}>{label}</Text>
  </View>
);

// ─── RFI Document ─────────────────────────────────────────────
/**
 * Premium RFI PDF template.
 *
 * @param {object} props
 * @param {object} props.rfi           - RFI record from project_rfis table
 * @param {string} props.projectName   - Project name
 * @param {string} props.projectNumber - Project number
 * @param {string} props.companyName   - Company name from user profile
 * @param {Array}  props.images        - Array of { name, path, signedUrl }
 * @param {string} [props.logoUrl]     - Company logo URL (optional)
 * @param {string} [props.language]    - Language code: 'en' or 'fr' (default: 'en')
 */
const RFIDocument = ({
  rfi,
  projectName,
  projectNumber,
  companyName,
  images = [],
  logoUrl,
  language = 'en',
}) => {
  const t       = labels?.[language] || labels?.en;
  const dateStr = formatDate(rfi?.created_at, language);
  const company = companyName  || 'Company';
  const project = projectName  || 'Project';
  const projNum = projectNumber ? `Project No. ${projectNumber}` : null;
  const rfiNum  = rfi?.rfi_number || 'RFI';

  return (
    <Document>
      <Page size="A4" style={styles?.page}>

        {/* ── Header ── */}
        <View style={styles?.header}>
          <View style={styles?.headerLeft}>
            {logoUrl ? (
              <Image src={logoUrl} style={styles?.logoImage} />
            ) : (
              <Text style={styles?.companyName}>{company}</Text>
            )}
            <Text style={styles?.projectName}>{project}</Text>
            {projNum ? <Text style={styles?.projectNumber}>{projNum}</Text> : null}
          </View>
          <View style={styles?.headerRight}>
            <Text style={styles?.rfiNumberLabel}>{t?.rfiNo}</Text>
            <Text style={styles?.rfiNumber}>{rfiNum}</Text>
            <Text style={styles?.rfiDate}>{dateStr}</Text>
          </View>
        </View>

        {/* ── Accent line ── */}
        <View style={styles?.accentLine} />

        {/* ── Title bar ── */}
        <View style={styles?.titleBar}>
          <Text style={styles?.titleText}>{t?.title}</Text>
        </View>

        {/* ── Body ── */}
        <View style={styles?.body}>

          {/* Subject + Profession */}
          <SectionLabel label={t?.details} />
          <View style={styles?.subjectProfessionRow}>
            <View style={styles?.subjectBox}>
              <Text style={styles?.fieldLabel}>{t?.subject}</Text>
              <Text style={rfi?.subject ? styles?.fieldValue : styles?.fieldValueEmpty}>
                {rfi?.subject || t?.noSubject}
              </Text>
            </View>
            <View style={styles?.professionBox}>
              <Text style={styles?.fieldLabel}>{t?.profession}</Text>
              <Text style={rfi?.profession ? styles?.fieldValue : styles?.fieldValueEmpty}>
                {rfi?.profession || '—'}
              </Text>
            </View>
          </View>

          {/* Question */}
          <View style={styles?.questionSection}>
            <SectionLabel label={t?.question} />
            <View style={styles?.questionBox}>
              <Text style={styles?.questionText}>
                {rfi?.question || t?.noQuestion}
              </Text>
            </View>
          </View>

          {/* Response */}
          <View style={styles?.responseSection}>
            <SectionLabel label={t?.response} />
            <View style={styles?.responseBox}>
              {/* empty */}
            </View>
          </View>

        </View>

        {/* ── Page break — Attachments always start on page 2 ── */}
        {images?.length > 0 ? (
          <>
            <View break />
            <View style={{ paddingHorizontal: 28, paddingTop: 20 }}>
              <SectionLabel label={`${t?.attachments} (${images?.length})`} />
              {images?.map((imgObj, idx) =>
                imgObj?.signedUrl ? (
                  <Image
                    key={idx}
                    src={imgObj?.signedUrl}
                    style={styles?.attachmentImage}
                  />
                ) : null
              )}
            </View>
          </>
        ) : null}

        {/* ── Footer ── */}
        <View style={styles?.footer} fixed>
          <Text style={styles?.footerCompany}>{company}</Text>
          <Text
            style={styles?.footerPage}
            render={({ pageNumber, totalPages }) => t?.page(pageNumber, totalPages)}
          />
        </View>

      </Page>
    </Document>
  );
};

export default RFIDocument;
